import {
  type ChangeEvent,
  Fragment,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';

import { FieldProps } from '@rjsf/utils';
import isEqual from 'lodash/isEqual';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';
import { parseEntityRef } from '@backstage/catalog-model';

import { useCatalogUserLookup } from '../../../hooks/useCatalogUserLookup';
import { InputGroupArrayCardUiOptions } from './schema';

/**
 * A row of data, usually used like key = userId | email, value = string or undefined
 */
type RowData = Record<string, string | undefined>;

/**
 * OwnerData object, to resemble user ID and Email of people.
 */
type OwnerData = {
  userId?: string;
  email?: string;
};

/**
 * FieldStatus type for input fields.
 */
type FieldStatus = 'default' | 'success' | 'error';

/**
 * A React component that renders an input group array card for managing a list of technical contacts.
 * This component is designed to work with the ODP scaffolder plugin and provides functionality
 * for adding, removing, and validating user IDs and emails. It also integrates with the ODP
 * identity API and catalog user lookup to fetch user information.
 *
 * @param props - The props for the component.
 * @param props.onChange - Callback function to handle changes in the form data.
 * @param props.formData - The current form data, representing an array of rows.
 * @param props.formContext - Context data passed to the form.
 * @param props.schema - The JSON schema for the field.
 * @param props.idSchema - The schema for generating unique IDs for the fields.
 * @param props.disabled - Whether the field is disabled.
 * @param props.readonly - Whether the field is read-only.
 * @param props.uiSchema - The UI schema for customizing the field's appearance.
 *
 * @returns A rendered React component for managing an array of input groups.
 *
 * @remarks
 * - The component pre-fills the first row with the logged-in user's information if available.
 * - It validates that at least two technical contacts are provided and that the logged-in user
 *   is listed as either a technical contact or an AI product owner.
 * - The component supports debounced lookups for user IDs and emails in the ODP catalog.
 */
export const InputGroupArrayCard = ({
  onChange,
  formData,
  formContext,
  schema,
  idSchema,
  disabled,
  readonly,
  uiSchema,
}: FieldProps<RowData[]>) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const lastEmittedValueRef = useRef<any>(
    Array.isArray(formData) ? formData : null,
  );

  const theme = useTheme();
  const identityApi = useApi(identityApiRef);

  const [localValues, setLocalValues] = useState<RowData[]>(
    Array.isArray(formData) ? formData : [],
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [minimumValuesError, setMinimumValuesError] = useState<string | null>(
    null,
  );
  const didPrefillRef = useRef(false);

  const [userInfo, setUserInfo] = useState<{
    identity: { userEntityRef: string };
    profile: { email?: string };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const mounted = useRef(true);

  const timersRef = useRef<
    Record<string, ReturnType<typeof setTimeout> | undefined>
  >({});
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});

  // Use FieldStatus for per-field state
  const [fieldStatus, setFieldStatus] = useState<Record<string, FieldStatus>>(
    {},
  );
  const [fieldHelpers, setFieldHelpers] = useState<Record<string, string>>({});
  const [focusedFields, setFocusedFields] = useState<Record<string, boolean>>(
    {},
  );
  const debounceMs = 500;

  const { findUserByUserId, findUserByEmail, isEmail } = useCatalogUserLookup();

  const backgroundColor = theme.palette.mode === 'dark' ? '#0D0D0D' : '#F4F4F4';
  const values: RowData[] = localValues;
  const uiOptions =
    ((uiSchema as any)?.['ui:options'] as InputGroupArrayCardUiOptions) ?? {};
  const ownerFieldName = uiOptions.ownerFieldName ?? 'AIProductOwner';
  const ownerRoleLabel = uiOptions.ownerRoleLabel ?? 'AI Product Owner';
  const owner: OwnerData | undefined = ownerFieldName
    ? formContext.formData?.[ownerFieldName]
    : undefined;

  const itemsSchema = (schema as any)?.items ?? {};
  const propsSchema = (itemsSchema?.properties ?? {}) as Record<
    string,
    { title?: string; format?: string; description?: string }
  >;
  const uiItems = (uiSchema as any)?.items ?? {};
  const order =
    (uiItems?.['ui:order'] as string[] | undefined) ??
    (uiOptions as any)?.fields;
  const lockFirstRow = uiOptions.lockFirstRow === true;

  // Extract const values for hidden fields (e.g. type: TECHNICAL_CONTACT) so they
  // are automatically injected into every row emitted via onChange.
  const constValues: Record<string, string> = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(propsSchema)
          .filter(k => (propsSchema[k] as any)?.const !== undefined)
          .map(k => [
            k,
            String(
              (propsSchema[k] as any)?.const ??
                (propsSchema[k] as any)?.default ??
                '',
            ),
          ]),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(propsSchema)],
  );

  // Exclude hidden / const-only fields from the visible key set.
  const allKeys = Object.keys(propsSchema).filter(k => {
    const uiWidget = (uiItems as any)?.[k]?.['ui:widget'];
    const hasConst = (propsSchema[k] as any)?.const !== undefined;
    return uiWidget !== 'hidden' && !hasConst;
  });
  let orderedKeys = allKeys;
  if (order?.length) {
    orderedKeys = [
      ...order.filter((k: string) => allKeys.includes(k)),
      ...allKeys.filter(k => !order.includes(k)),
    ];
  }
  const selectedKeys = orderedKeys.slice(0, 2);
  const userIdKey = 'userId';
  const emailKey = 'email';

  const labels: Record<string, string> = Object.fromEntries(
    selectedKeys.map(k => [k, propsSchema[k]?.title ?? k]),
  );
  const types: Record<string, string> = Object.fromEntries(
    selectedKeys.map(k => [
      k,
      propsSchema[k]?.format === 'email' ? 'email' : 'text',
    ]),
  );

  const minItems = typeof schema.minItems === 'number' ? schema.minItems : 0;
  const maxItems =
    typeof schema.maxItems === 'number' ? schema.maxItems : undefined;

  const canAdd =
    !disabled &&
    !readonly &&
    (maxItems === undefined || values.length < maxItems);

  /** Cleanup timers on unmount
   */
  useEffect(() => {
    const timersSnapshot = timersRef.current;
    return () => {
      Object.values(timersSnapshot).forEach(t => t && clearTimeout(t));
    };
  }, []);

  /**
   * Called on mount to fetch user info from identity API.
   */
  useEffect(() => {
    mounted.current = true;
    const fetchUserInfo = async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const profile = await identityApi.getProfileInfo();
        if (mounted.current) {
          setUserInfo({ identity, profile });
          setLoading(false);
        }
      } catch (err) {
        if (mounted.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    };
    fetchUserInfo();
    return () => {
      mounted.current = false;
    };
  }, [identityApi]);

  /**
   * Called when the user info or values change -> Pre-fill the first row with logged in user's info.
   */
  useEffect(() => {
    if (didPrefillRef.current || !userInfo) {
      return;
    }

    const firstRow = values?.[0];
    const firstRowIsCompletelyEmpty =
      !firstRow ||
      selectedKeys.every(key => {
        const value = firstRow[key];
        return value === '' || value === undefined;
      });

    if (values.length === 0 || firstRowIsCompletelyEmpty) {
      const { name: userId } = parseEntityRef(userInfo.identity.userEntityRef);
      const email = userInfo.profile?.email ?? '';

      const prefilledRow: RowData = {
        [userIdKey]: userId,
        [emailKey]: email,
      };
      selectedKeys.forEach(k => {
        prefilledRow[k] ??= '';
      });

      const rowsToSet: RowData[] = [prefilledRow];
      if (maxItems === undefined || maxItems > 1) {
        const emptyRow: RowData = selectedKeys.reduce((acc, k) => {
          acc[k] = '';
          return acc;
        }, {} as RowData);
        rowsToSet.push(emptyRow);
      }

      setLocalValues(rowsToSet);
      didPrefillRef.current = true;
    } else {
      didPrefillRef.current = true;
    }
  }, [userInfo, values, selectedKeys, maxItems]);

  /**
   * Called when the values, owner or user info change -> Validate that at least 2 technical contacts are provided
   * and that the logged in user is either listed as technical contact or AI product owner.
   */
  useEffect(() => {
    if (!userInfo) {
      return;
    }

    const { name: loggedInUserId } = parseEntityRef(
      userInfo.identity.userEntityRef,
    );
    const normalizedLoggedInUserId = loggedInUserId.toLowerCase();

    const firstTwoRowsValid = values.slice(0, minItems).every(row => {
      return (
        typeof row?.[userIdKey] === 'string' &&
        row[userIdKey].trim().length > 0 &&
        typeof row?.[emailKey] === 'string' &&
        row[emailKey].trim().length > 0
      );
    });

    if (firstTwoRowsValid) {
      setMinimumValuesError(null);
    }

    const isUserInPluginList = values.some(
      row =>
        (typeof row?.[userIdKey] === 'string'
          ? row?.[userIdKey].toLowerCase()
          : '') === normalizedLoggedInUserId,
    );

    const isUserInOwnerField =
      (owner?.userId ?? '').toLowerCase() === normalizedLoggedInUserId;

    if (!isUserInPluginList && !isUserInOwnerField) {
      const errorMsg = ownerRoleLabel
        ? `You must be listed either as a Technical Contact or ${ownerRoleLabel}.`
        : 'You must be listed as a Technical Contact.';
      setValidationError(errorMsg);

      if (lastEmittedValueRef.current !== undefined) {
        lastEmittedValueRef.current = undefined;
        onChangeRef.current(undefined);
      }
    } else {
      setValidationError(null);
      // Inject hidden const-value fields (e.g. type) into every row before emitting.
      const withConst = values.map(r => ({ ...constValues, ...r }));
      if (!isEqual(lastEmittedValueRef.current, withConst)) {
        lastEmittedValueRef.current = withConst;
        onChangeRef.current(withConst);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, owner, userInfo, minItems, ownerRoleLabel, constValues]);

  /**
   * Sets the Cell loading indicator of an input field.
   * @param row The row index.
   * @param key The key of the field to check, e.g. 'userId'.
   * @param v True, if the indicator should be displayed.
   */
  function setCellLoading(row: number, key: string, v: boolean) {
    setLoadingMap(prev => ({ ...prev, [`${row}:${key}`]: v }));
  }

  /**
   * Sets status and helper text for a field.
   * @param row The row index.
   * @param key The key of the field to check, e.g. 'userId'.
   * @param status The FieldStatus of the row field.
   * @param text The text to display, if for instance the status is 'error'.
   */
  function setFieldState(
    row: number,
    key: string,
    status: FieldStatus,
    text: string,
  ) {
    const fk = `${row}:${key}`;
    setFieldStatus(prev => ({ ...prev, [fk]: status }));
    setFieldHelpers(prev => ({ ...prev, [fk]: text }));
  }

  /**
   * Handles the user ID lookup, if user ID field was changed.
   * @param rowIndex The row index.
   * @param value The user ID value.
   * @returns A Promise that resolves when the lookup is done.
   */
  async function handleUserIdLookup(rowIndex: number, value: string) {
    if (value.trim().length === 0) return;
    setCellLoading(rowIndex, userIdKey, true);
    setFieldState(
      rowIndex,
      userIdKey,
      'default',
      'Searching user in catalog...',
    );

    const user = await findUserByUserId(value).finally(() =>
      setCellLoading(rowIndex, userIdKey, false),
    );

    if (!user) {
      const defaultText = propsSchema[userIdKey]?.description ?? '';
      setFieldState(rowIndex, userIdKey, 'default', defaultText);
      return;
    }

    const email = (user as any)?.spec?.profile?.email ?? '';
    if (email) {
      setLocalValues(prev => {
        const next = [...prev];
        const row = { ...(next[rowIndex] ?? {}) };
        if (row[emailKey] !== email) row[emailKey] = email;
        next[rowIndex] = row;
        return next;
      });

      setFieldState(
        rowIndex,
        userIdKey,
        'success',
        'Success! This user ID has been found in the catalog.',
      );
      setFieldState(
        rowIndex,
        emailKey,
        'success',
        'Success! This email has been obtained from the catalog.',
      );
    } else {
      // User found but no email -> show message without error highlighting
      setFieldState(
        rowIndex,
        userIdKey,
        'default',
        'Error. User found but no email available. Please enter your email.',
      );
    }
  }

  /**
   * Handles the email lookup, if email field was changed.
   * @param rowIndex The row index.
   * @param value The email value.
   * @returns A Promise that resolves when the lookup is done.
   */
  async function handleEmailLookup(rowIndex: number, value: string) {
    if (!isEmail(value)) return;
    setCellLoading(rowIndex, emailKey, true);
    setFieldState(
      rowIndex,
      emailKey,
      'default',
      'Searching user in catalog...',
    );

    const user = await findUserByEmail(value).finally(() =>
      setCellLoading(rowIndex, emailKey, false),
    );

    if (!user) {
      // On error: reset description
      const defaultText = propsSchema[emailKey]?.description ?? '';
      setFieldState(rowIndex, emailKey, 'default', defaultText);
      return;
    }

    const id = user.metadata?.name ?? '';
    if (id) {
      setLocalValues(prev => {
        const next = [...prev];
        const row = { ...(next[rowIndex] ?? {}) };
        if (row[userIdKey] !== id) row[userIdKey] = id;
        next[rowIndex] = row;
        return next;
      });

      // On success: mark fields as success and set messages
      setFieldState(
        rowIndex,
        emailKey,
        'success',
        'Success! This email has been found in the catalog.',
      );
      setFieldState(
        rowIndex,
        userIdKey,
        'success',
        'Success! This user ID has been obtained from the catalog.',
      );
    }
  }

  /**
   * Handles the linked lookup when either user ID or email field was changed.
   * @param rowIndex The row index.
   * @param inputKey The key of the field to check, e.g. 'userId' or 'email'.
   * @param value The value of the field.
   * @returns A Promise that resolves when the lookup is done.
   */
  async function handleLinkedLookup(
    rowIndex: number,
    inputKey: string,
    value: string,
  ) {
    if (!value.trim()) return;
    if (inputKey === userIdKey) {
      handleUserIdLookup(rowIndex, value);
    } else if (inputKey === emailKey) {
      handleEmailLookup(rowIndex, value);
    }
  }

  /**
   * Checks if a value matches a pattern.
   * @param value The value to check.
   * @param pattern The regex pattern string.
   * @returns True if matches, false otherwise.
   */
  function matchesPattern(value: string, pattern?: string): boolean {
    if (!pattern) return true;
    try {
      return new RegExp(pattern).test(value);
    } catch (e) {
      return false;
    }
  }

  /**
   * Checks if a focused field is empty and mandatory, and sets error state if so.
   * @param rowIndex The row index.
   * @param key The key of the field to check, e.g. 'userId'.
   * @returns True if the field is focused but empty and mandatory, false otherwise.
   */
  const checkFocusedButEmptyFieldAndMandatory = (
    rowIndex: number,
    key: string,
  ) => {
    const next = [...values];
    const row = { ...(next[rowIndex] ?? {}) };
    const value = typeof row[key] === 'string' ? (row[key] ?? '') : '';
    const pattern = (propsSchema[key] as any)?.pattern;
    let errorMessage = '';

    if (key === emailKey) {
      errorMessage =
        (propsSchema[key] as any)?.errorMessage ??
        'Error. Inputted text is in an invalid format. Domain must be either @mercedes-benz.com or @extaccount.com.';
    } else {
      errorMessage =
        (propsSchema[key] as any)?.errorMessage ??
        'Error. Inputted text is in an invalid format. User ID must only contain alphanumeric characters (letters and numbers).';
    }

    if (value.trim().length === 0) {
      setFieldState(rowIndex, key, 'error', 'Error. This is a required field.');
      if (rowIndex < minItems) {
        setMinimumValuesError(
          `Error. Provide user ID and email of at least ${minItems} Technical Contact${minItems > 1 ? 's' : ''}.`,
        );
      }
      return true;
    }

    // Pattern matching validation
    if (pattern && !matchesPattern(value, pattern)) {
      setFieldState(rowIndex, key, 'error', errorMessage);
      return true;
    }

    return false;
  };

  /**
   * Handles focus event for a field, marking it as focused for label shrink.
   * @param rowIndex The row index.
   * @param key The field key.
   */
  const handleFocus = (rowIndex: number, key: string) => () => {
    const fk = `${rowIndex}:${key}`;
    setFocusedFields(prev => ({ ...prev, [fk]: true }));
  };

  /**
   * Handles blur event for a field, clearing focus state and running validation.
   * @param rowIndex The row index.
   * @param key The field key.
   */
  const handleBlur = (rowIndex: number, key: string) => () => {
    const fk = `${rowIndex}:${key}`;
    setFocusedFields(prev => ({ ...prev, [fk]: false }));
    checkFocusedButEmptyFieldAndMandatory(rowIndex, key);
  };

  /**
   * Handles the change event for input fields.
   * @param rowIndex The row index.
   * @param key The key of the field to check, e.g. 'userId'.
   * @returns A function that handles the change event.
   */
  const handleChange =
    (rowIndex: number, key: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const next = [...values];
      const row = { ...(next[rowIndex] ?? {}) };
      row[key] = e.target.value;
      next[rowIndex] = row;

      if (!checkFocusedButEmptyFieldAndMandatory(rowIndex, key)) {
        const defaultText = propsSchema[key]?.description ?? '';
        setFieldState(rowIndex, key, 'default', defaultText);
      }

      setLocalValues(next);

      if (key === userIdKey || key === emailKey) {
        const timerKey = `${rowIndex}:${key}`;
        if (timersRef.current[timerKey])
          clearTimeout(timersRef.current[timerKey]);
        timersRef.current[timerKey] = setTimeout(() => {
          handleLinkedLookup(rowIndex, key, e.target.value);
        }, debounceMs);
      }
    };

  /**
   * Rebuilds a state map after row removal by dropping the removed row's entries
   * and shifting all subsequent row indices down by one.
   * @param map The state map keyed by `${rowIndex}:${key}`.
   * @param removedIndex The index of the removed row.
   */
  function rebuildMapAfterRemoval<T>(
    map: Record<string, T>,
    removedIndex: number,
  ): Record<string, T> {
    const result: Record<string, T> = {};
    for (const [fk, val] of Object.entries(map)) {
      const colonPos = fk.indexOf(':');
      const rowIdx = parseInt(fk.slice(0, colonPos), 10);
      const fieldKey = fk.slice(colonPos + 1);
      if (rowIdx === removedIndex) continue;
      const newIdx = rowIdx > removedIndex ? rowIdx - 1 : rowIdx;
      result[`${newIdx}:${fieldKey}`] = val;
    }
    return result;
  }

  /**
   * Handles adding a new row to the list.
   */
  function handleAdd() {
    const emptyRow: RowData = selectedKeys.reduce((acc, k) => {
      acc[k] = '';
      return acc;
    }, {} as RowData);
    setLocalValues([...(values ?? []), emptyRow]);
  }

  /**
   * Handles removing a row from the list.
   * @param rowIndex
   */
  function handleRemove(rowIndex: number) {
    const next = [...values];
    next.splice(rowIndex, 1);
    setLocalValues(next);
    setFieldStatus(prev => rebuildMapAfterRemoval(prev, rowIndex));
    setFieldHelpers(prev => rebuildMapAfterRemoval(prev, rowIndex));
    setLoadingMap(prev => rebuildMapAfterRemoval(prev, rowIndex));
    setFocusedFields(prev => rebuildMapAfterRemoval(prev, rowIndex));
  }

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <Alert severity="error">Error loading user data: {error.message}</Alert>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor,
        padding: 2,
        borderRadius: 1,
      }}
    >
      {schema.title && (
        <Box component="h2" sx={{ marginBottom: 1, marginTop: 0 }}>
          {schema.title}
        </Box>
      )}

      {schema.description && (
        <Box sx={{ marginBottom: 1 }}>{schema.description}</Box>
      )}

      {(schema.title || schema.description) && (
        <Divider sx={{ marginBottom: 2, marginTop: 1 }} />
      )}

      {validationError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {validationError}
        </Alert>
      )}

      {minimumValuesError && minimumValuesError.trim().length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {minimumValuesError}
        </Alert>
      )}

      <Stack spacing={3}>
        {values.map((row, i) => {
          const canRemove = !disabled && !readonly && values.length > minItems;
          return (
            <Fragment key={`${idSchema.$id}_row_${i}`}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Stack direction="column" spacing={2} sx={{ flex: 1 }}>
                  {selectedKeys.map(key => {
                    const placeholder = uiItems?.[key]?.['ui:placeholder'];
                    const fk = `${i}:${key}`;
                    const status = fieldStatus[fk] ?? 'default';
                    return (
                      <TextField
                        key={`${idSchema.$id}_${i}_${key}`}
                        id={`${idSchema.$id}_${i}_${key}`}
                        label={labels[key]}
                        placeholder={placeholder}
                        InputLabelProps={{
                          shrink: focusedFields[fk] || !!row?.[key],
                        }}
                        required
                        value={row?.[key] ?? ''}
                        onChange={handleChange(i, key)}
                        onFocus={handleFocus(i, key)}
                        onBlur={handleBlur(i, key)}
                        disabled={
                          disabled || readonly || (lockFirstRow && i === 0)
                        }
                        type={types[key]}
                        variant="outlined"
                        fullWidth
                        // control red error state
                        error={status === 'error'}
                        // set green color when success
                        color={status === 'success' ? 'success' : undefined}
                        // keep green visible without focus
                        focused={status === 'success'}
                        helperText={
                          fieldHelpers[fk] ??
                          propsSchema[key]?.description ??
                          ''
                        }
                        InputProps={{
                          endAdornment: loadingMap[fk] ? (
                            <InputAdornment position="end">
                              <CircularProgress size={18} />
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                    );
                  })}
                </Stack>

                <IconButton
                  aria-label="Remove"
                  onClick={() => handleRemove(i)}
                  disabled={!canRemove || (lockFirstRow && i === 0)}
                  size="medium"
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Stack>
              {i < values.length - 1 && <Divider sx={{ my: 2 }} />}
            </Fragment>
          );
        })}
      </Stack>

      {(minItems !== 1 || maxItems !== 1) && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={handleAdd}
            disabled={!canAdd || disabled || readonly}
            size="small"
            startIcon={<AddCircleOutlineIcon />}
          >
            Add technical contact
          </Button>
        </Box>
      )}
    </Box>
  );
};
